import { motion } from "framer-motion";
import {
  ShieldCheck,
  UserPlus,
  CheckCircle2,
  Clock3,
  KeyRound,
  Layers3,
  ArrowUpRight,
  Activity,
} from "lucide-react";
import MockFrame from "./MockFrame";

const fadeUp = {
  hidden: { opacity: 0, y: 10 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.45, delay: 0.08 + i * 0.05, ease: [0.22, 1, 0.36, 1] },
  }),
};

const requests = [
  { initials: "SC", name: "Sarah Chen", role: "Eng · Platform", resource: "Production DB", status: "pending" },
  { initials: "MR", name: "Marcus Rivera", role: "Finance Ops", resource: "Billing Console", status: "approved" },
  { initials: "AP", name: "Aisha Patel", role: "Data Science", resource: "Customer Warehouse", status: "review" },
  { initials: "JT", name: "Jordan Tanaka", role: "Support · L2", resource: "Admin Portal", status: "pending" },
];

const STATUS_STYLES = {
  approved: { label: "Approved", dot: "#34d399", text: "rgba(167, 243, 208, 0.95)", bg: "rgba(52, 211, 153, 0.10)", border: "rgba(52, 211, 153, 0.28)" },
  pending: { label: "Pending", dot: "#fbbf24", text: "rgba(253, 230, 138, 0.95)", bg: "rgba(251, 191, 36, 0.10)", border: "rgba(251, 191, 36, 0.28)" },
  review: { label: "In Review", dot: "#22d3ee", text: "rgba(165, 243, 252, 0.95)", bg: "rgba(34, 211, 238, 0.10)", border: "rgba(34, 211, 238, 0.28)" },
};

function StatCard({ icon: Icon, label, value, sub, accent, i }) {
  return (
    <motion.div
      variants={fadeUp}
      custom={i}
      className="rounded-xl border border-white/5 bg-white/[0.025] p-3"
    >
      <div className="flex items-center justify-between">
        <span
          className="flex h-7 w-7 items-center justify-center rounded-lg ring-1"
          style={{ backgroundColor: accent.bg, color: accent.primary, borderColor: accent.border }}
        >
          <Icon className="h-3.5 w-3.5" strokeWidth={2.4} />
        </span>
        <ArrowUpRight className="h-3 w-3 text-white/25" />
      </div>
      <p className="mt-2.5 text-[9px] font-semibold uppercase tracking-[0.14em] text-white/40">
        {label}
      </p>
      <p className="mt-0.5 text-[1.05rem] font-bold leading-none text-white tabular-nums">
        {value}
      </p>
      <p
        className="mt-1 text-[10px] font-semibold tabular-nums"
        style={{ color: accent.softText }}
      >
        {sub}
      </p>
    </motion.div>
  );
}

function Sparkline({ accent }) {
  return (
    <svg viewBox="0 0 200 60" className="h-12 w-full overflow-visible" aria-hidden>
      <defs>
        <linearGradient id="accuro-spark" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={accent.primary} stopOpacity="0.55" />
          <stop offset="100%" stopColor={accent.primary} stopOpacity="0" />
        </linearGradient>
      </defs>
      <motion.path
        d="M0,42 L20,38 L40,40 L60,32 L80,26 L100,30 L120,20 L140,24 L160,14 L180,18 L200,8 L200,60 L0,60 Z"
        fill="url(#accuro-spark)"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, amount: 0.4 }}
        transition={{ duration: 0.6, delay: 0.25 }}
      />
      <motion.path
        d="M0,42 L20,38 L40,40 L60,32 L80,26 L100,30 L120,20 L140,24 L160,14 L180,18 L200,8"
        fill="none"
        stroke={accent.primary}
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
        initial={{ pathLength: 0 }}
        whileInView={{ pathLength: 1 }}
        viewport={{ once: true, amount: 0.4 }}
        transition={{ duration: 1.2, ease: "easeOut" }}
      />
      <motion.circle
        cx="200"
        cy="8"
        r="3"
        fill={accent.primary}
        initial={{ scale: 0 }}
        whileInView={{ scale: 1 }}
        viewport={{ once: true, amount: 0.4 }}
        transition={{ duration: 0.3, delay: 1.1 }}
      />
    </svg>
  );
}

export default function AccuroVisual({ accent }) {
  return (
    <MockFrame
      domain="accuro360.velozent.com / governance"
      accent={accent}
      tabs={["Overview", "Access Requests", "Policies", "Audit"]}
      activeTab={1}
    >
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.25 }}
        className="space-y-3"
      >
        <div className="grid grid-cols-3 gap-2.5">
          <StatCard
            icon={UserPlus}
            label="Active identities"
            value="12,847"
            sub="+8.2% this week"
            accent={accent}
            i={0}
          />
          <StatCard
            icon={Clock3}
            label="Pending approvals"
            value="23"
            sub="4 SLA-critical"
            accent={accent}
            i={1}
          />
          <StatCard
            icon={ShieldCheck}
            label="Active policies"
            value="156"
            sub="100% compliant"
            accent={accent}
            i={2}
          />
        </div>

        <motion.div
          variants={fadeUp}
          custom={3}
          className="rounded-xl border border-white/5 bg-white/[0.025]"
        >
          <div className="flex items-center justify-between border-b border-white/5 px-3 py-2">
            <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-white/50">
              Recent access requests
            </p>
            <span className="hidden text-[9.5px] font-medium text-white/35 sm:inline">
              Last 24h · auto-routed
            </span>
          </div>
          <ul className="divide-y divide-white/5">
            {requests.map((r, i) => {
              const s = STATUS_STYLES[r.status];
              return (
                <motion.li
                  key={r.name}
                  variants={fadeUp}
                  custom={4 + i}
                  className="flex items-center gap-2.5 px-3 py-2"
                >
                  <span
                    className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[9.5px] font-bold ring-1"
                    style={{
                      backgroundColor: accent.bg,
                      color: accent.light,
                      borderColor: accent.border,
                    }}
                  >
                    {r.initials}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[11px] font-semibold text-white/90">
                      {r.name}
                    </p>
                    <p className="truncate text-[9.5px] text-white/40">{r.role}</p>
                  </div>
                  <div className="hidden min-w-0 flex-1 sm:block">
                    <p className="flex items-center gap-1 truncate text-[10px] text-white/55">
                      <KeyRound
                        className="h-2.5 w-2.5 shrink-0"
                        style={{ color: accent.primary }}
                      />
                      {r.resource}
                    </p>
                  </div>
                  <span
                    className="inline-flex items-center gap-1 rounded-md border px-1.5 py-[2px] text-[9px] font-bold uppercase tracking-[0.1em]"
                    style={{ borderColor: s.border, backgroundColor: s.bg, color: s.text }}
                  >
                    <span
                      className="h-1 w-1 rounded-full"
                      style={{ backgroundColor: s.dot }}
                    />
                    {s.label}
                  </span>
                </motion.li>
              );
            })}
          </ul>
        </motion.div>

        <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
          <motion.div
            variants={fadeUp}
            custom={8}
            className="rounded-xl border border-white/5 bg-white/[0.025] p-3"
          >
            <div className="flex items-center justify-between">
              <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-white/50">
                Access requests · 7d
              </p>
              <span
                className="inline-flex items-center gap-1 text-[10px] font-bold tabular-nums"
                style={{ color: accent.light }}
              >
                <ArrowUpRight className="h-3 w-3" />
                12.4%
              </span>
            </div>
            <Sparkline accent={accent} />
          </motion.div>

          <motion.div
            variants={fadeUp}
            custom={9}
            className="rounded-xl border border-white/5 bg-white/[0.025] p-3"
          >
            <div className="flex items-center justify-between">
              <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-white/50">
                Approval workflow
              </p>
              <span className="text-[9.5px] font-medium text-white/35">3 stages</span>
            </div>
            <div className="mt-2.5 space-y-1.5">
              {[
                { label: "Manager", state: "done", icon: CheckCircle2 },
                { label: "Security review", state: "active", icon: Layers3 },
                { label: "Owner sign-off", state: "queued", icon: Clock3 },
              ].map((step, i) => {
                const StepIcon = step.icon;
                const isDone = step.state === "done";
                const isActive = step.state === "active";
                return (
                  <div key={step.label} className="flex items-center gap-2">
                    <span
                      className="flex h-5 w-5 shrink-0 items-center justify-center rounded-md"
                      style={{
                        backgroundColor: isDone
                          ? "rgba(52, 211, 153, 0.15)"
                          : isActive
                          ? accent.bg
                          : "rgba(255,255,255,0.04)",
                        color: isDone
                          ? "#34d399"
                          : isActive
                          ? accent.primary
                          : "rgba(255,255,255,0.35)",
                      }}
                    >
                      <StepIcon className="h-2.5 w-2.5" strokeWidth={2.6} />
                    </span>
                    <div className="flex-1">
                      <p className="text-[10.5px] font-medium text-white/80">
                        {step.label}
                      </p>
                      <div className="mt-1 h-1 rounded-full bg-white/5">
                        <motion.div
                          className="h-full rounded-full"
                          style={{
                            backgroundColor: isDone
                              ? "#34d399"
                              : isActive
                              ? accent.primary
                              : "transparent",
                          }}
                          initial={{ width: 0 }}
                          whileInView={{
                            width: isDone ? "100%" : isActive ? "55%" : "0%",
                          }}
                          viewport={{ once: true, amount: 0.4 }}
                          transition={{ duration: 0.8, delay: 0.4 + i * 0.15 }}
                        />
                      </div>
                    </div>
                    {isActive && (
                      <span
                        className="h-1.5 w-1.5 shrink-0 animate-pulse rounded-full"
                        style={{ backgroundColor: accent.primary }}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </motion.div>
        </div>

        <motion.div
          variants={fadeUp}
          custom={10}
          className="flex items-center justify-between rounded-xl border border-white/5 bg-white/[0.02] px-3 py-2"
        >
          <div className="flex items-center gap-2">
            <Activity className="h-3.5 w-3.5" style={{ color: accent.primary }} />
            <p className="text-[10.5px] text-white/65">
              <span className="font-semibold text-white/85">Audit:</span>{" "}
              Role <span style={{ color: accent.light }}>“Vendor Admin”</span>{" "}
              expired for 4 identities
            </p>
          </div>
          <span className="hidden text-[9.5px] font-medium text-white/35 sm:inline">
            2 min ago
          </span>
        </motion.div>
      </motion.div>
    </MockFrame>
  );
}
